import {WorkoutPowerQualityCard} from '../components/WorkoutPowerQualityCard';
import { useLocation } from 'react-router-dom';
import { TRAINING_ZONE_COLORS, TRAINING_ZONE_GRADIENTS, trainingZoneColor } from '../utils/trainingZones';
import { NmLoader } from '../components/NmLoader';
import { WorkoutChromeIconButton } from '../components/workout/WorkoutChromeIconButton';
import { ActionButton, ActionToggleGroup } from '../components/ui/ActionButton';
import '../styles/workout-detail-system.css';
import { useTranslation } from 'react-i18next';
import { calendarWorkoutState, localCalendarDay } from '../utils/calendarWorkoutState';
import '../styles/workout-header-identity.css';
import {WorkoutShareModal} from '../components/WorkoutShareModal';
import {parseStreamData} from '../utils/workoutStream';
import {IoShareSocialOutline} from 'react-icons/io5';
import { isRunningWorkout } from '../utils/runningWorkoutMetrics';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { workouts, calendarActions } from '../services/api';
import type { WorkoutDetail } from '../services/api';
import { workoutStressLabel } from '../utils/workoutStressLabel';
import { WorkoutBuilderPage, type WorkoutBuilderEditConfig } from './WorkoutBuilderPage';
import { WorkoutFormModal, type WorkoutFormInitialValues } from './WorkoutFormModal';
import { AddToLibraryModal, type AddToLibraryWorkoutData } from '../components/AddToLibraryModal';
import { useConfirm } from '../components/NmConfirmDialog';
import { useToast } from '../context/ToastContext';
import { useComparisonNavigation } from '../context/ComparisonNavigationContext';
import { apiStructureToBuilderSteps, structureBarHeightPct, stepTargetRange, stepZoneColor } from '../utils/workoutStructure';
import type { ApiStructStep } from '../utils/workoutStructure';
import { useAthleteContext } from '../context/AthleteContext';
import { useAthleteMetrics } from '../hooks/useAthleteMetrics';
import { MobileWorkoutSummaryTab } from '../features/workout/MobileWorkoutSummaryTab';
import { WorkoutTypeIcon } from '../components/WorkoutTypeIcon';
import { getWorkoutTypeDisplayName } from '../utils/workoutTypeDisplay';
import { MobileWorkoutMapTab } from '../features/workout/MobileWorkoutMapTab';
import { loadWorkoutWithDetails } from '../features/workout/workoutDetailLoad';
import { isCompletedWorkout } from '../features/workout/workoutFeelingConstants';
import { logWorkoutFeeling } from '../features/workout/workoutFeelingDebug';
import { formatApiDateCompact, formatApiDateWeekday } from '../utils/calendarPickerLocale';
import { useWorkoutStream } from '../features/workout/useWorkoutStream';
import { NmTabNav } from '../components/NmTabNav';
import { TrainingLoadBadge } from '../components/TrainingLoadBadge';
import {
  fetchStructuredFitFile,
  resolveStructuredFitSportType,
  triggerFitFileDownload,
} from '../features/workout/structuredFitExport';
import { WorkoutChromeHeader } from '../components/workout/WorkoutChromeHeader';
import { NmDatePickerModal } from '../components/NmDatePickerModal';
import { isoToJalaliKey, jalaliKeyToIso } from '../utils/jalali';
import {
  IoCloudDownloadOutline,
  IoCreateOutline,
  IoMenuOutline,
  IoFolderOpenOutline,
  IoGitCompareOutline,
  IoTrashOutline,
} from 'react-icons/io5';

// ── Design tokens — vo2 design system ────────────────────────────────────────
const BG         = '#E8ECF2';
const SUNKEN     = '#DDE2EB';
const PINK       = 'var(--palette-brand-ink)';
const PINK_DEEP  = 'var(--palette-brand-ink)';
const PINK_TEXT  = 'var(--palette-brand-ink)';  // active tab text (Workout Detail)
const GREEN      = '#3CC67D';
const GOLD       = '#F5B740';
const ORANGE     = '#FF8A65';
const TEXT       = '#1A1F2B';
const TEXT_MID   = '#6B7282';
const TEXT_MUTED = '#9AA1B1';
const TEXT_FAINT = '#B0B8C8';

// Dark neumorphic – Workout Summary Light (floating cards)
const SH_HERO  = '-5px -5px 14px rgba(80,100,140,.30), 5px 5px 14px rgba(0,0,0,.65)';
const SH_MD    = '-4px -4px 10px rgba(80,100,140,.28), 4px 4px 10px rgba(0,0,0,.60)';
const SH_SM    = '-3px -3px 7px rgba(80,100,140,.28), 3px 3px 7px rgba(0,0,0,.55)';
const SH_IN    = 'inset 3px 3px 7px rgba(0,0,0,.55), inset -2px -2px 6px rgba(80,100,140,.22)';
const SH_IN_SM = 'inset 2px 2px 5px rgba(0,0,0,.5), inset -1px -1px 4px rgba(80,100,140,.2)';
// Light neumorphic – Workout Detail (stats tiles and sub-cards)
const SH_LIGHT   = '-5px -5px 12px rgba(255,255,255,.9), 5px 5px 12px rgba(163,177,198,.55)';
const SH_TILE    = '-4px -4px 9px rgba(255,255,255,.9), 4px 4px 9px rgba(163,177,198,.55)';
const SH_ICON    = '-2px -2px 5px rgba(255,255,255,.9), 2px 2px 5px rgba(163,177,198,.5)';
const SH_ICON_SM = '-1px -1px 3px rgba(255,255,255,.95), 1px 1px 3px rgba(163,177,198,.5)';
// Tab bar – Workout Detail
const SH_TAB_BAR = 'inset 4px 4px 8px rgba(163,177,198,.55), inset -4px -4px 8px rgba(255,255,255,.95)';
const SH_TAB_ACT = '-3px -3px 6px rgba(255,255,255,.9), 3px 3px 6px rgba(163,177,198,.5)';

// HR zone colours
const HR_Z_GRADS = TRAINING_ZONE_GRADIENTS;
const HR_Z_DOTS = [...TRAINING_ZONE_COLORS];

// Power zones chart scale max (% FTP)
const PWR_MAX = 150;

interface StructTarget {
  minValue?: number | string;
  maxValue?: number | string;
  type?: string;
}

interface StructBlock {
  type?: string;
  length?: { value: number | string; unit?: string };
  steps?: StructStep[];
  intensityClass?: string;
  intensity?: string;
  name?: string;
  description?: string;
}

interface StructStep {
  name?: string;
  description?: string;
  intensityClass?: string;
  intensity?: string;
  length?: { value: number | string; unit?: string };
  type?: string;
  openDuration?: string;
  targets?: StructTarget[];
}

/** Local step shape for structure tab — colours via shared zone helper. */
function stepBarColor(step: StructStep): string {
  return stepZoneColor(step as ApiStructStep);
}

function normIntensityClass(step: StructStep): string {
  return (step.intensityClass ?? step.intensity ?? '').toLowerCase();
}

function intensityLabel(step: StructStep): string {
  const ic = normIntensityClass(step);
  const map: Record<string, string> = {
    warmup: 'WU', active: 'ACT', rest: 'RST', recovery: 'REC',
    cooldown: 'CD', interval: 'INT', race: 'RACE',
  };
  if (map[ic]) return map[ic];
  if (ic) return ic.slice(0, 4).toUpperCase();
  return '';
}

function isRampStep(step: StructStep): boolean {
  const ic = normIntensityClass(step);
  if (ic === 'warmup' || ic === 'cooldown') return true;
  const { min, max } = stepTargetRange(step);
  return Math.abs(max - min) >= 8;
}

/** Convert a length object to total seconds, accounting for unit. */
function stepLengthSecs(length?: { value: number | string; unit?: string }): number {
  if (!length) return 0;
  const v = Number(length.value) || 0;
  const u = (length.unit ?? '').toLowerCase();
  if (u === 'minute' || u === 'minutes' || u === 'min') return v * 60;
  if (u === 'hour' || u === 'hours') return v * 3600;
  // default: treat as seconds
  return v;
}

// PW zone colours
const PW_Z_GRADS = TRAINING_ZONE_GRADIENTS;
const PW_Z_DOTS = [...TRAINING_ZONE_COLORS];

type TabKey = 'summary' | 'analyze';

// ── Helpers ───────────────────────────────────────────────────────────────────

function secToHMS(s: number): string {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function fmtInterval(s: number): string {
  if (s < 60) return `${s}s`;
  const m = s / 60;
  if (s < 3600) return Number.isInteger(m) ? `${m}m` : `${Math.round(m)}m`;
  return `${(s / 3600).toFixed(1)}h`;
}


// ── Hero card (Workout Summary Light) ─────────────────────────────────────────

function HeroCard({ d }: { d: WorkoutDetail }) {
  const dur  = d.total_time != null ? secToHMS(Number(d.total_time)) : (d.total_time_planned != null ? secToHMS(Number(d.total_time_planned)) : null);
  const km   = d.distance != null ? (d.distance / 1000).toFixed(1) : null;
  const tss  = d.tss_actual != null ? String(Math.round(Number(d.tss_actual))) : null;
  const ifV  = d.if_actual != null ? Number(d.if_actual).toFixed(2) : null;
  const sportName = d.workout_type?.english_name ?? d.workout_type?.name ?? '';
  const metrics = [
    { val: km,  unit: 'km',  color: GREEN, load: null },
    { val: tss, unit: workoutStressLabel(d), color: GOLD, load: d.training_load },
    { val: ifV, unit: 'IF',  color: PINK, load: null },
  ];

  return (
    <div style={{ borderRadius: 24, background: BG, boxShadow: SH_HERO, overflow: 'hidden' }}>
      {/* top row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px 12px' }}>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: TEXT_MUTED }}>
          RECORDED WORKOUT
        </span>
        {sportName ? (
          <div style={{ width: 38, height: 38, borderRadius: 12, background: 'color-mix(in srgb, var(--text-accent) 15%, transparent)', color: PINK, display: 'grid', placeItems: 'center' }}>
            <WorkoutTypeIcon name={sportName} size={18} />
          </div>
        ) : null}
      </div>
      {/* duration */}
      <div style={{ padding: '10px 18px 16px', textAlign: 'center', borderTop: '1px solid rgba(163,177,198,.2)' }}>
        <div style={{ fontSize: 42, fontWeight: 800, letterSpacing: '-0.04em', color: TEXT, fontVariantNumeric: 'tabular-nums' }}>
          {dur ?? '—'}
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: TEXT_MUTED, letterSpacing: '0.1em', marginTop: 2 }}>
          TOTAL DURATION
        </div>
      </div>
      {/* 3-col metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 1, background: 'rgba(163,177,198,.15)', borderTop: '1px solid rgba(163,177,198,.2)' }}>
        {metrics.map(({ val, unit, color, load }) => (
          <div key={unit} style={{ padding: '14px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, background: BG }}>
            <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums', color, lineHeight: 1 }}>
              {val ?? '—'}
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: unit === 'rTss' ? 'none' : 'uppercase', color: TEXT_MUTED }}>
              {unit}
            </div>
            <TrainingLoadBadge load={load} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Zone strip (Workout Summary Light) ───────────────────────────────────────

function ZoneStrip({ title, zones, grads, dots }: { title: string; zones: any[]; grads: [string, string][]; dots: string[] }) {
  if (!zones.length) return null;
  return (
    <div style={{ borderRadius: 20, background: BG, padding: '14px 16px', boxShadow: SH_MD }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: TEXT_MUTED, letterSpacing: '0.08em' }}>{title}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          {zones.map((z, i) => (
            <span key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: trainingZoneColor(Number(z.zone ?? i + 1)), display: 'inline-block' }} />
          ))}
        </div>
      </div>
      {/* zone blocks */}
      <div style={{ display: 'flex', gap: 3, height: 28, borderRadius: 10, overflow: 'hidden', boxShadow: SH_IN_SM }}>
        {zones.map((z, i) => {
          const pct = z.percent_in_zone ?? 1;
          const c1 = trainingZoneColor(Number(z.zone ?? i + 1));
          const c2 = c1;
          return (
            <div key={i} style={{ flex: Math.max(pct, 0.5), borderRadius: 4, background: `linear-gradient(180deg,${c1},${c2})`, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 3, fontSize: 8, fontWeight: 800, color: 'var(--training-zone-label)', transition: 'flex 400ms cubic-bezier(0.22,1,0.36,1)' }}>
              Z{z.zone ?? i + 1}
            </div>
          );
        })}
      </div>
      {/* time labels */}
      <div style={{ display: 'flex', gap: 3, marginTop: 8 }}>
        {zones.map((z, i) => (
          <div key={i} style={{ flex: Math.max(z.percent_in_zone ?? 1, 0.5), textAlign: 'center', fontSize: 9, fontWeight: 700, color: TEXT_FAINT, fontVariantNumeric: 'tabular-nums' }}>
            {z.time_in_seconds > 0 ? fmtInterval(z.time_in_seconds) : '—'}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Section card – dark shadow wrapper (Workout Summary Light) ────────────────

function SectionCard({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <div style={{ borderRadius: 22, background: BG, overflow: 'hidden', boxShadow: SH_MD }}>
      <div style={{ padding: '14px 16px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: TEXT, letterSpacing: '-0.01em' }}>{title}</span>
        {sub && <span style={{ fontSize: 11, color: TEXT_MUTED, fontWeight: 600 }}>{sub}</span>}
      </div>
      {children}
    </div>
  );
}

// ── Stats card – light shadow wrapper (Workout Detail) ────────────────────────

function StatsCard({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <div style={{ borderRadius: 22, background: BG, overflow: 'hidden', boxShadow: SH_LIGHT }}>
      <div style={{ padding: '14px 16px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: TEXT, letterSpacing: '-0.01em' }}>{title}</span>
        {sub && <span style={{ fontSize: 11, color: TEXT_MUTED, fontWeight: 600 }}>{sub}</span>}
      </div>
      {children}
    </div>
  );
}

// ── Detail stats grid (Workout Detail — 2-col icon+label+value) ───────────────

function StatsGrid({ cells }: { cells: { icon: React.ReactNode; label: string; value: string; unit?: string; accent?: string }[] }) {
  if (!cells.length) return null;
  return (
    <div style={{ borderRadius: 22, background: BG, overflow: 'hidden', boxShadow: SH_LIGHT }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
        {cells.map((c, i) => (
          <div key={i} style={{ padding: '10px 12px', borderBottom: '1px solid rgba(163,177,198,.18)', borderLeft: i % 2 === 0 ? '1px solid rgba(163,177,198,.18)' : undefined, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 24, height: 24, borderRadius: 7, flexShrink: 0, background: BG, display: 'grid', placeItems: 'center', color: TEXT_MUTED, boxShadow: SH_ICON_SM }}>
              {c.icon}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
              <span style={{ fontSize: 9, fontWeight: 700, color: TEXT_MUTED, letterSpacing: '0.05em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.label}</span>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: c.accent ?? TEXT, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', lineHeight: 1 }}>{c.value}</span>
                {c.unit && <span style={{ fontSize: 9, fontWeight: 600, color: TEXT_FAINT, letterSpacing: '0.04em' }}>{c.unit}</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Comparison table (Workout Summary Light — comp-table) ─────────────────────

interface CompRow { label: string; planned: string | number | null; actual: string | number | null; delta?: string | null; over?: boolean }

function CompTable({ rows }: { rows: CompRow[] }) {
  if (!rows.length) return null;
  const th: React.CSSProperties = { padding: '8px 14px 10px', fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', color: TEXT_MUTED, textAlign: 'center', background: SUNKEN, borderBottom: '1px solid rgba(163,177,198,.2)' };
  const td: React.CSSProperties = { padding: '11px 14px', fontSize: 14, fontWeight: 700, color: TEXT_MID, textAlign: 'center', fontVariantNumeric: 'tabular-nums', borderBottom: '1px solid rgba(163,177,198,.15)' };
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={{ ...th, textAlign: 'left', color: TEXT_FAINT }}></th>
          <th style={th}>Planned</th>
          <th style={th}>Actual</th>
          <th style={th}>Diff</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const isLast = i === rows.length - 1;
          const border = isLast ? 'none' : '1px solid rgba(163,177,198,.15)';
          return (
            <tr key={i}>
              <td style={{ ...td, fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', color: TEXT_FAINT, textAlign: 'left', borderBottom: border }}>{r.label}</td>
              <td style={{ ...td, borderBottom: border }}>{r.planned ?? '—'}</td>
              <td style={{ ...td, borderBottom: border }}>
                {r.actual != null ? (
                  <span style={{ padding: '2px 8px', borderRadius: 8, fontWeight: 800, background: r.over === false ? 'rgba(60,198,125,.1)' : 'color-mix(in srgb, var(--text-accent) 10%, transparent)', color: r.over === false ? GREEN : PINK }}>
                    {r.actual}
                  </span>
                ) : '—'}
              </td>
              <td style={{ ...td, borderBottom: border }}>
                {r.delta ? (
                  <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 6px', borderRadius: 6, background: r.over ? 'rgba(255,138,101,.15)' : 'rgba(60,198,125,.15)', color: r.over ? ORANGE : GREEN }}>
                    {r.delta}
                  </span>
                ) : '—'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── Min / Avg / Max table (Workout Detail — mam-table / Workout Summary Light — stats-table) ──

interface MamRow { label: string; min: string | null; avg: string | null; max: string | null }

function MamTable({ rows }: { rows: MamRow[] }) {
  if (!rows.length) return null;
  const th: React.CSSProperties = { padding: '8px 14px 10px', fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textAlign: 'center', background: SUNKEN, borderBottom: '1px solid rgba(163,177,198,.2)' };
  const td: React.CSSProperties = { padding: '10px 14px', fontSize: 15, fontWeight: 800, textAlign: 'center', fontVariantNumeric: 'tabular-nums', borderBottom: '1px solid rgba(163,177,198,.15)' };
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={{ ...th, textAlign: 'left', color: TEXT_MUTED }}></th>
          <th style={{ ...th, color: GREEN }}>Min</th>
          <th style={{ ...th, color: TEXT }}>Avg</th>
          <th style={{ ...th, color: PINK }}>Max</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const isLast = i === rows.length - 1;
          const b = isLast ? 'none' : '1px solid rgba(163,177,198,.15)';
          return (
            <tr key={i}>
              <td style={{ ...td, fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: TEXT_MUTED, textAlign: 'left', borderBottom: b }}>{r.label}</td>
              <td style={{ ...td, color: GREEN, borderBottom: b }}>{r.min ?? '—'}</td>
              <td style={{ ...td, color: TEXT, borderBottom: b }}>{r.avg ?? '—'}</td>
              <td style={{ ...td, color: PINK, borderBottom: b }}>{r.max ?? '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── Notes card (Workout Summary Light — inset) ────────────────────────────────

function NotesCard({ label, text }: { label: string; text?: string | null }) {
  if (!text) return null;
  return (
    <div style={{ borderRadius: 20, background: BG, padding: '16px', boxShadow: SH_IN }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: TEXT_MUTED, letterSpacing: '0.1em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={PINK} strokeWidth="2" strokeLinecap="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
        {label}
      </div>
      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: TEXT_MID, lineHeight: 1.6 }}>{text}</p>
    </div>
  );
}

// ── Structured Workout View — full-width timeline + step list ────────────────

/** Flatten API blocks into timeline segments (repeat blocks expanded). */
function flattenStructureBlocks(blocks: StructBlock[]): StructStep[] {
  const out: StructStep[] = [];
  blocks.forEach(block => {
    const reps = block.type === 'repetition' ? (Number(block.length?.value) || 1) : 1;
    const steps = block.steps ?? [];
    if (steps.length === 0) {
      out.push(block as unknown as StructStep);
      return;
    }
    for (let r = 0; r < reps; r++) {
      steps.forEach(step => out.push(step));
    }
  });
  return out;
}

/** Group blocks for step list — repeats shown once, not expanded. */
interface DisplayGroup {
  kind: 'step' | 'repeat';
  steps: StructStep[];
  repeatCount: number;
  blockIdx: number;
}

function groupStructureBlocks(blocks: StructBlock[]): DisplayGroup[] {
  return blocks.map((block, bi) => {
    const steps = block.steps ?? [];
    const isRepeat = block.type === 'repetition' && (Number(block.length?.value) || 1) > 1;
    if (isRepeat && steps.length > 0) {
      return { kind: 'repeat' as const, steps, repeatCount: Number(block.length?.value) || 1, blockIdx: bi };
    }
    if (steps.length === 1) {
      return { kind: 'step' as const, steps: [steps[0]], repeatCount: 1, blockIdx: bi };
    }
    if (steps.length > 1) {
      return { kind: 'repeat' as const, steps, repeatCount: 1, blockIdx: bi };
    }
    return { kind: 'step' as const, steps: [block as unknown as StructStep], repeatCount: 1, blockIdx: bi };
  });
}

/** Full-width SVG power profile — bar heights from FTP % targets. */
function StructurePowerChart({ steps }: { steps: StructStep[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const [cw, setCw] = useState(400);

  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(e => setCw(Math.floor(e[0].contentRect.width)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  if (!steps.length) return null;

  const H = 100;
  const totalDur = steps.reduce((a, s) => a + stepLengthSecs(s.length), 0) || 1;
  const W = Math.max(cw, 100);

  // Scale Y-axis to fit all target values (some workouts exceed 150% FTP)
  const scaleMax = Math.max(
    PWR_MAX,
    ...steps.flatMap(s => {
      const { min, max } = stepTargetRange(s);
      return [min, max];
    }),
  );

  const pctToH = (pct: number) => Math.min(Math.max((pct / scaleMax) * H, 0), H);

  let cx = 0;
  const bars = steps.map((step, i) => {
    const dur = stepLengthSecs(step.length);
    const w = Math.max((dur / totalDur) * W, 2);
    const { min, max } = stepTargetRange(step);
    const color = stepBarColor(step);
    const ox = cx;
    cx += w;

    let shape: React.ReactNode;
    if (isRampStep(step)) {
      const pct = structureBarHeightPct(step as ApiStructStep);
      const bh = Math.max(pctToH(pct), 2);
      shape = (
        <rect x={ox + 0.5} y={H - bh} width={Math.max(w - 1, 1)} height={bh}
          rx={Math.min(3, (w - 1) / 2)} fill={color} opacity={0.88} />
      );
    } else {
      const mid = (min + max) / 2;
      const bh = Math.max(pctToH(mid), 2);
      shape = (
        <rect x={ox + 0.5} y={H - bh} width={Math.max(w - 1, 1)} height={bh}
          rx={Math.min(3, (w - 1) / 2)} fill={color} opacity={0.88} />
      );
    }

    return (
      <g key={i}>
        {shape}
        <title>{`${step.name ?? ''} · ${secToHMS(dur)} · ${min}–${max}% FTP`}</title>
      </g>
    );
  });

  const gridLines = [50, 100].filter(p => p <= scaleMax);
  if (scaleMax > 100 && !gridLines.includes(100)) gridLines.push(100);

  return (
    <div ref={ref} style={{ width: '100%', height: H, borderRadius: 10, overflow: 'hidden', background: SUNKEN, boxShadow: SH_IN_SM }}>
      {cw > 20 && (
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" style={{ display: 'block', overflow: 'hidden' }}>
          {gridLines.map(p => {
            const y = H - pctToH(p);
            return (
              <line key={p} x1={0} y1={y} x2={W} y2={y}
                stroke="rgba(163,177,198,.35)" strokeWidth={p === 100 ? 1 : 0.5}
                strokeDasharray={p === 100 ? undefined : '3,4'} />
            );
          })}
          {bars}
        </svg>
      )}
    </div>
  );
}

function StructuredWorkoutView({ structure }: { structure: { structure: any[]; primaryLengthMetric?: string; primaryIntensityMetric?: string } }) {
  const blocks: StructBlock[] = structure.structure ?? [];
  const timelineSteps = flattenStructureBlocks(blocks);
  const displayGroups = groupStructureBlocks(blocks);
  const totalDur = timelineSteps.reduce((sum, step) => sum + stepLengthSecs(step.length), 0);

  return (
    <div style={{ borderRadius: 22, background: BG, overflow: 'hidden', boxShadow: SH_LIGHT }}>
      <div style={{ padding: '14px 16px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: TEXT, letterSpacing: '-0.01em' }}>Workout Structure</span>
        <span style={{ fontSize: 11, color: TEXT_MUTED, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{secToHMS(totalDur)}</span>
      </div>

      {/* Power profile — full width, variable bar heights */}
      <div style={{ padding: '0 14px 10px' }}>
        <StructurePowerChart steps={timelineSteps} />
      </div>

      {/* Step list — grouped (repeat blocks collapsed to one template) */}
      <div style={{ padding: '0 14px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {displayGroups.map((group, gi) => {
          const groupDur = group.steps.reduce((a, s) => a + stepLengthSecs(s.length), 0);

          return (
            <React.Fragment key={gi}>
              {group.kind === 'repeat' && group.repeatCount > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 4px 2px', marginTop: gi > 0 ? 4 : 0 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={PINK} strokeWidth="2.3" strokeLinecap="round">
                    <path d="M17 1l4 4-4 4" /><path d="M3 11V9a4 4 0 0 1 4-4h14" />
                    <path d="M7 23l-4-4 4-4" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
                  </svg>
                  <span style={{ fontSize: 10, fontWeight: 800, color: TEXT_MUTED, letterSpacing: '0.04em' }}>
                    REPEAT ×{group.repeatCount}
                  </span>
                  <span style={{ fontSize: 9, color: TEXT_FAINT, fontVariantNumeric: 'tabular-nums' }}>
                    {secToHMS(groupDur * group.repeatCount)}
                  </span>
                </div>
              )}

              {group.steps.map((step, si) => {
                const dur = stepLengthSecs(step.length);
                const { min, max } = stepTargetRange(step);
                const color = stepBarColor(step);
                const il = intensityLabel(step);

                return (
                  <div key={si} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 8px 6px 10px', borderRadius: 8,
                    background: SUNKEN, position: 'relative', overflow: 'hidden',
                  }}>
                    <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: color }} />

                    {il && (
                      <span style={{
                        fontSize: 8, fontWeight: 800, padding: '1px 5px', borderRadius: 5,
                        background: color + '22', color, flexShrink: 0, letterSpacing: '0.04em',
                      }}>
                        {il}
                      </span>
                    )}

                    <span style={{ fontSize: 11, fontWeight: 700, color: TEXT, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {step.name ?? step.description ?? `Step ${si + 1}`}
                    </span>

                    {(min > 0 || max > 0) && (
                      <span style={{ fontSize: 9, fontWeight: 600, color: TEXT_FAINT, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                        {min}–{max}%
                      </span>
                    )}

                    <span style={{ fontSize: 10, fontWeight: 700, color: TEXT_MUTED, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                      {secToHMS(dur)}
                    </span>
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ── Summary tab ───────────────────────────────────────────────────────────────

function SummaryTab({ d }: { d: WorkoutDetail }) {
  const { t } = useTranslation();
  // Comparison rows
  const compRows: CompRow[] = [];
  if (d.total_time != null || d.total_time_planned != null) {
    const p = d.total_time_planned ? Number(d.total_time_planned) : null;
    const a = d.total_time ? Number(d.total_time) : null;
    let delta: string | null = null; let over = false;
    if (p && a) { const dm = Math.round((a - p) / 60); if (dm !== 0) { delta = `${dm > 0 ? '+' : ''}${dm}m`; over = dm > 0; } }
    compRows.push({ label: 'Time', planned: p ? secToHMS(p) : null, actual: a ? secToHMS(a) : null, delta, over });
  }
  if (d.distance != null || d.distance_planned != null) {
    compRows.push({ label: 'Distance', planned: d.distance_planned != null ? `${(Number(d.distance_planned) / 1000).toFixed(1)} km` : null, actual: d.distance != null ? `${(d.distance / 1000).toFixed(1)} km` : null, over: false });
  }
  if (d.tss_actual != null || d.tss_planned != null) {
    const p = d.tss_planned ? Number(d.tss_planned) : null;
    const a = d.tss_actual ? Number(d.tss_actual) : null;
    let delta: string | null = null; let over = false;
    if (p != null && a != null) { const df = Math.round(a - p); if (df !== 0) { delta = `${df > 0 ? '+' : ''}${df}`; over = Math.abs(df) > p * 0.15; } }
    compRows.push({ label: workoutStressLabel(d), planned: p != null ? Math.round(p) : null, actual: a != null ? Math.round(a) : null, delta, over });
  }
  if (d.if_actual != null || d.if_planned != null) {
    const p = d.if_planned ? Number(d.if_planned) : null;
    const a = d.if_actual ? Number(d.if_actual) : null;
    let delta: string | null = null; let over = false;
    if (p != null && a != null) { const df = a - p; if (Math.abs(df) >= 0.01) { delta = `${df >= 0 ? '+' : ''}${df.toFixed(2)}`; over = Math.abs(df) > 0.15; } }
    compRows.push({ label: 'IF', planned: p?.toFixed(2) ?? null, actual: a?.toFixed(2) ?? null, delta, over });
  }
  if (d.calories != null || d.calories_planned != null) {
    compRows.push({ label: 'Calories', planned: d.calories_planned ? Math.round(parseInt(d.calories_planned)) : null, actual: d.calories ? Math.round(parseInt(d.calories)) : null, over: false });
  }

  // MAM rows
  const mma = d.details?.min_max_avg;
  const fmt = (v: unknown, f = 0) => { const n = Number(v); return v != null && isFinite(n) ? n.toFixed(f) : null; };
  const mamRows: MamRow[] = [];
  if (mma?.heart_rate) mamRows.push({ label: 'bpm',  min: fmt(mma.heart_rate.min), avg: fmt(mma.heart_rate.avg), max: fmt(mma.heart_rate.max) });
  if (mma?.power)      mamRows.push({ label: 'W',    min: fmt(mma.power.min),      avg: fmt(mma.power.avg),      max: fmt(mma.power.max) });
  if (mma?.cadence)    mamRows.push({ label: isRunningWorkout(d) ? `${t('runningMetrics.cadence')} (${t('runningMetrics.cadenceUnit')})` : 'rpm',  min: fmt(mma.cadence.min),    avg: fmt(mma.cadence.avg),    max: fmt(mma.cadence.max) });
  if (mma?.speed)      mamRows.push({ label: 'km/h', min: fmt(mma.speed.min, 1),   avg: fmt(mma.speed.avg, 1),   max: fmt(mma.speed.max, 1) });

  // Detail stats cells (Workout Detail — stats-grid, 2-col)
  type StatCell = { icon: React.ReactNode; label: string; value: string; unit?: string; accent?: string };
  const ico = (path: string) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 11, height: 11 }}>
      <path d={path} />
    </svg>
  );
  const statCells: StatCell[] = [];
  if (d.if_actual   != null) statCells.push({ icon: ico('M13 2L3 14h9l-1 8 10-12h-9l1-8z'), label: 'Training Intensity',  value: Number(d.if_actual).toFixed(2),  accent: PINK_TEXT });
  if (d.work        != null) statCells.push({ icon: ico('M12 20V10M18 20V4M6 20v-4'),        label: 'Total Work',          value: String(d.work),                  unit: 'kJ' });
  if (d.np          != null) statCells.push({ icon: ico('M22 12 18 12 15 21 9 3 6 12 2 12'), label: 'Normalized Power',    value: String(d.np),                    unit: 'W' });
  if (d.vi          != null) statCells.push({ icon: ico('M12 2a10 10 0 1 0 10 10M12 6v6l4 2'), label: 'Variability Index', value: String(d.vi) });
  if (d.ef          != null) statCells.push({ icon: ico('M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z'), label: 'Efficiency Factor', value: String(d.ef) });
  if (d.pwhr        != null) statCells.push({ icon: ico('M4 12h16M12 4v16'), label: 'Pw:HR Decoupling', value: Number(d.pwhr).toFixed(1), unit: '%' });
  if (d.wkg         != null) statCells.push({ icon: ico('M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 0 0 1 1h3m10-11l2 2'),   label: 'W/kg',             value: String(d.wkg),                   unit: 'W/kg', accent: '#E0981A' });
  if (d.total_ascent  != null) statCells.push({ icon: ico('M18 15l-6-6-6 6'), label: 'Ascent',                          value: String(d.total_ascent),          unit: 'm', accent: GREEN });
  if (d.total_descent != null) statCells.push({ icon: ico('M6 9l6 6 6-6'),   label: 'Descent',                         value: String(d.total_descent),         unit: 'm', accent: PINK });
  if (d.vam         != null) statCells.push({ icon: ico('M5 3l14 9-14 9V3z'),                                          label: 'VAM',              value: String(d.vam),                   unit: 'm/h' });
  if (d.grade       != null) statCells.push({ icon: ico('M3 17l4-8 4 5 3-3 4 6'),                                      label: 'Grade',            value: String(d.grade),                 unit: '%' });

  const hrZ = d.details?.zones?.hr_zones ?? [];
  const pwZ = d.details?.zones?.pw_zones ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Hero card */}
      <HeroCard d={d} />

      {/* Zone strips */}
      {hrZ.length > 0 && <ZoneStrip title="HR ZONE DISTRIBUTION" zones={hrZ} grads={HR_Z_GRADS} dots={HR_Z_DOTS} />}
      {pwZ.length > 0 && <ZoneStrip title="POWER ZONE DISTRIBUTION" zones={pwZ} grads={PW_Z_GRADS} dots={PW_Z_DOTS} />}

      {/* Plan comparison */}
      {compRows.length > 0 && (
        <SectionCard title="Plan vs Actual" sub="Planned ← Actual">
          <CompTable rows={compRows} />
        </SectionCard>
      )}

      {/* Min / Avg / Max — light shadow per Workout Detail */}
      {mamRows.length > 0 && (
        <StatsCard title="Detailed Stats" sub="Min · Avg · Max">
          <MamTable rows={mamRows} />
        </StatsCard>
      )}

      {/* Extra stats grid (Workout Detail — stats-grid) */}
      {statCells.length > 0 && <StatsGrid cells={statCells} />}

      {/* Workout structure — full-width timeline + step list */}
      {d.structure?.structure?.length ? (
        <StructuredWorkoutView structure={d.structure} />
      ) : null}

      {/* Description */}
      <NotesCard label="DESCRIPTION" text={d.description} />

      {/* Comments */}
      {d.comments?.length > 0 && (
        <SectionCard title={`Comments (${d.comments.length})`}>
          <div style={{ padding: '8px 14px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {d.comments.slice(0, 5).map((c: any) => (
              <div key={c.id} style={{ padding: '8px 10px', borderRadius: 10, background: SUNKEN, boxShadow: SH_IN_SM }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: TEXT }}>{c.user?.full_name}</span>
                  <span style={{ fontSize: 9, color: TEXT_FAINT, marginLeft: 'auto' }}>{c.created_at?.slice(0, 10)}</span>
                </div>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: TEXT_MID, lineHeight: 1.5 }}>{c.description}</p>
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}

// ── Zones tab ─────────────────────────────────────────────────────────────────

function ZonesTab({ d }: { d: WorkoutDetail }) {
  const hrZ = d.details?.zones?.hr_zones ?? [];
  const pwZ = d.details?.zones?.pw_zones ?? [];
  if (!hrZ.length && !pwZ.length) {
    return <div style={{ padding: 40, textAlign: 'center', color: TEXT_MUTED, fontSize: 13, fontWeight: 600 }}>No zone data</div>;
  }

  const ZoneDetail = ({ zones, grads, dots, title }: { zones: any[]; grads: [string, string][]; dots: string[]; title: string }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <ZoneStrip title={title} zones={zones} grads={grads} dots={dots} />
      <StatsCard title={title.split(' ')[0] + ' Zones'}>
        <div style={{ padding: '10px 14px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {zones.map((z: any, i: number) => {
            const c1 = trainingZoneColor(Number(z.zone ?? i + 1));
            const c2 = c1;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 22, fontSize: 10, fontWeight: 800, color: trainingZoneColor(Number(z.zone ?? i + 1)), textAlign: 'right', flexShrink: 0 }}>Z{z.zone ?? i + 1}</span>
                <div style={{ flex: 1, height: 16, borderRadius: 5, overflow: 'hidden', background: SUNKEN, boxShadow: SH_IN_SM }}>
                  <div style={{ width: `${Math.max(z.percent_in_zone ?? 0, 0.3)}%`, height: '100%', background: `linear-gradient(90deg,${c1},${c2})`, borderRadius: 5 }} />
                </div>
                <span style={{ width: 48, fontSize: 10, fontWeight: 700, color: TEXT_MID, textAlign: 'right', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                  {z.time_in_seconds > 0 ? fmtInterval(z.time_in_seconds) : '—'}
                </span>
                <span style={{ width: 36, fontSize: 9, color: TEXT_FAINT, textAlign: 'right', flexShrink: 0 }}>
                  {(z.percent_in_zone ?? 0).toFixed(1)}%
                </span>
              </div>
            );
          })}
        </div>
      </StatsCard>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {hrZ.length > 0 && <ZoneDetail zones={hrZ} grads={HR_Z_GRADS} dots={HR_Z_DOTS} title="HR ZONE DISTRIBUTION" />}
      {pwZ.length > 0 && <ZoneDetail zones={pwZ} grads={PW_Z_GRADS} dots={PW_Z_DOTS} title="POWER ZONE DISTRIBUTION" />}
    </div>
  );
}

// ── Laps tab ──────────────────────────────────────────────────────────────────

function LapsTab({ d }: { d: WorkoutDetail }) {
  const { t } = useTranslation();
  const laps = d.details?.lap_data ?? [];
  if (!laps.length) return <div style={{ padding: 40, textAlign: 'center', color: TEXT_MUTED, fontSize: 13, fontWeight: 600 }}>No lap data</div>;

  type Col = { key: string; label: string; fmt?: (v: any) => string };
  const allCols: Col[] = [
    { key: 'lap',                label: '#' },
    { key: 'total_elapsed_time', label: 'Time', fmt: v => v ? secToHMS(Number(v)) : '—' },
    { key: 'total_distance',     label: 'm',    fmt: v => v ? String(Math.round(Number(v))) : '—' },
    { key: 'avg_heart_rate',     label: 'HR',    fmt: v => v ? String(Math.round(Number(v))) : '—' },
    { key: 'avg_power',          label: 'W',     fmt: v => v ? String(Math.round(Number(v))) : '—' },
    { key: 'avg_speed',          label: 'km/h',  fmt: v => v ? Number(v).toFixed(1) : '—' },
    { key: 'avg_cadence',        label: isRunningWorkout(d) ? `${t('runningMetrics.cadence')} (${t('runningMetrics.cadenceUnit')})` : 'rpm',   fmt: v => v ? String(Math.round(Number(v))) : '—' },
  ];
  const cols = allCols.filter(c => c.key === 'lap' || laps.some((l: any) => l[c.key] != null));
  const thS: React.CSSProperties = { padding: '8px 10px', fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', color: TEXT_MUTED, background: SUNKEN, textAlign: 'center', textTransform: 'uppercase', borderBottom: '1px solid rgba(163,177,198,.2)', whiteSpace: 'nowrap' };
  const tdS: React.CSSProperties = { padding: '8px 10px', fontSize: 12, fontWeight: 700, textAlign: 'center', fontVariantNumeric: 'tabular-nums', color: TEXT_MID, borderBottom: '1px solid rgba(163,177,198,.12)' };

  return (
    <div style={{ borderRadius: 22, background: BG, overflow: 'hidden', boxShadow: SH_MD }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 280 }}>
          <thead>
            <tr>{cols.map(c => <th key={c.key} style={{ ...thS, textAlign: c.key === 'lap' ? 'left' : 'center' }}>{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {laps.map((lap: any, i: number) => (
              <tr key={i} style={{ background: i % 2 ? 'rgba(163,177,198,.06)' : 'transparent' }}>
                {cols.map(c => (
                  <td key={c.key} style={{ ...tdS, textAlign: c.key === 'lap' ? 'left' : 'center', fontWeight: c.key === 'lap' ? 800 : 700, color: c.key === 'lap' ? TEXT : TEXT_MID, borderBottom: i < laps.length - 1 ? '1px solid rgba(163,177,198,.12)' : 'none' }}>
                    {c.fmt ? c.fmt(lap[c.key]) : lap[c.key] ?? '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── WorkoutDetailPanel ────────────────────────────────────────────────────────

/** Build library exercise payload from a structured workout detail. */
function workoutDetailToLibraryData(d: WorkoutDetail): AddToLibraryWorkoutData {
  return {
    title: d.title,
    description: d.description,
    workout_type_id: d.workout_type?.id,
    structure: d.structure ?? undefined,
    tss_planned: d.tss_planned != null ? Number(d.tss_planned) : null,
    if_planned: d.if_planned != null ? Number(d.if_planned) : null,
    total_time_planned: d.total_time_planned != null ? Number(d.total_time_planned) : null,
    distance_planned: d.distance_planned != null ? Number(d.distance_planned) : null,
    calories_planned: d.calories_planned != null ? Number(d.calories_planned) : null,
  };
}

function workoutDetailToFormInitial(d: WorkoutDetail): WorkoutFormInitialValues {
  const pre = d.comments?.find(c => c.comment_type === 1);
  return {
    title: d.title,
    description: d.description,
    total_time_planned: d.total_time_planned,
    distance_planned: d.distance_planned,
    tss_planned: d.tss_planned,
    if_planned: d.if_planned,
    calories_planned: d.calories_planned,
    total_time: d.total_time,
    distance: d.distance,
    tss_actual: d.tss_actual,
    if_actual: d.if_actual,
    calories: d.calories,
    preComment: pre?.description ?? null,
    structure: d.structure ?? null,
    heart_rate_minimum: d.heart_rate_minimum,
    heart_rate_maximum: d.heart_rate_maximum,
    heart_rate_average: d.heart_rate_average,
    power_average: d.power_average,
    max_power: d.max_power,
    avg_cadence: d.avg_cadence,
    max_cadence: d.max_cadence,
    avg_speed: d.avg_speed,
    max_speed: d.max_speed,
    min_max_avg: d.details?.min_max_avg,
  };
}

export function WorkoutDetailPanel({
  workoutId,
  initialSection,
  userId,
  onClose,
  onDeleted,
  onUpdated,
}: {
  workoutId: number;
  initialSection?: 'records';
  userId?: number;
  onClose: () => void;
  onDeleted?: () => void;
  onUpdated?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const openedLocationKey = useRef(location.key);
  useEffect(() => {
    if (location.key !== openedLocationKey.current) onClose();
  }, [location.key, onClose]);

  const confirm = useConfirm();
  const { showToast } = useToast();
  const comparisonNav = useComparisonNavigation();
  const { isCoach } = useAthleteContext();
  const { ftp: athleteFtp, maxHr: athleteMaxHr, ltHr: athleteLtHr, thresholdSpeedMps: athleteThresholdSpeedMps } =
    useAthleteMetrics(userId ?? null);
  const recordScrollRef = useRef<HTMLDivElement>(null);
  const recordFocusedId = useRef<number | null>(null);
  const [data, setData]         = useState<WorkoutDetail | null>(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [tab, setTab]           = useState<TabKey>('summary');
  const [editMode, setEditMode] = useState<'builder' | 'form' | null>(null);
  const suppressBackdropCloseRef = useRef(false);
  const [deleting, setDeleting] = useState(false);
  const downloadFitBusy = useRef(false);
  const [showAddToLibrary, setShowAddToLibrary] = useState(false);
  const [librarySaved, setLibrarySaved] = useState(false);
  const [titleEditing, setTitleEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [titleSaving, setTitleSaving] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [dateSaving, setDateSaving] = useState(false);
  const [fitDownloadLoading, setFitDownloadLoading] = useState(false);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [shareOpen,setShareOpen]=useState(false);
  const [relatedWorkoutId, setRelatedWorkoutId] = useState<number | null>(null);
  const headerMenuRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const workout = await loadWorkoutWithDetails(workoutId);
      setData(workout);
      logWorkoutFeeling('workout loaded', {
        workoutId,
        total_time: workout.total_time,
        tss_actual: workout.tss_actual,
        feeling: workout.feeling,
        rpe: workout.rpe,
        isCompleted: isCompletedWorkout(workout),
        feelingSaveEnabled: isCompletedWorkout(workout),
      });
    } catch {
      setError(t('workoutDetail.loadFailed'));
    } finally { setLoading(false); }
  }, [workoutId, t]);

  useEffect(() => { void load(); setTab('summary'); setHeaderMenuOpen(false); }, [load]);

  useEffect(() => {
    if (!titleEditing) setTitleDraft(data?.title ?? '');
  }, [data?.title, titleEditing]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !editMode) onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, editMode]);

  useEffect(() => {
    if (!headerMenuOpen) return;
    const closeMenu = (event: PointerEvent) => {
      if (!headerMenuRef.current?.contains(event.target as Node)) setHeaderMenuOpen(false);
    };
    document.addEventListener('pointerdown', closeMenu);
    return () => document.removeEventListener('pointerdown', closeMenu);
  }, [headerMenuOpen]);

  const exitEditMode = useCallback(() => {
    suppressBackdropCloseRef.current = true;
    setEditMode(null);
    window.setTimeout(() => {
      suppressBackdropCloseRef.current = false;
    }, 400);
  }, []);

  const handleBackdropClose = useCallback(() => {
    if (suppressBackdropCloseRef.current || editMode) return;
    onClose();
  }, [editMode, onClose]);

  const handleEditStructure = useCallback(() => {
    if (!data?.structure?.structure?.length) return;
    setEditMode('builder');
  }, [data]);

  const handleEdit = useCallback(() => {
    if (!data) return;
    setEditMode(data.structure?.structure?.length ? 'builder' : 'form');
  }, [data]);

  const handleEditSaved = useCallback(() => {
    setEditMode(null);
    void load();
    onUpdated?.();
  }, [load, onUpdated]);

  const handleCommentSubmit = useCallback(async (text: string) => {
    if (!data) return;
    await workouts.addComment({ workout_id: data.id, comment_type: 2, description: text });
    await load();
  }, [data, load]);

  const fitBusy = fitDownloadLoading;

  const handleDownloadStructuredFit = useCallback(async () => {
    if (!data?.id || downloadFitBusy.current) return;
    downloadFitBusy.current = true;
    setFitDownloadLoading(true);
    try {
      if (data.fit_file) {
        const blob = await workouts.downloadFit(data.id);
        triggerFitFileDownload(blob, `workout-${data.id}.fit`);
      } else {
        const sportType = resolveStructuredFitSportType(data.workout_type?.id);
        const { filename, blob } = await fetchStructuredFitFile(data.id, sportType);
        triggerFitFileDownload(blob, filename);
      }
      showToast(t('workoutDetail.fit.downloadSuccess'));
    } catch {
      showToast(t('workoutDetail.fit.downloadFailed'));
    } finally {
      downloadFitBusy.current = false;
      setFitDownloadLoading(false);
    }
  }, [data, fitBusy, showToast, t]);

  const handleDeleteComment = useCallback(async (commentId: number) => {
    await workouts.deleteComment(commentId);
    await load();
  }, [load]);

  const handleFeelingSave = useCallback(
    async (payload: { feeling: number | null; rpe: number | null }) => {
      if (!data) return;
      await workouts.updateWorkout(data.id, {
        feeling: payload.feeling != null ? String(payload.feeling) : null,
        rpe: payload.rpe != null ? String(payload.rpe) : null,
      });
      await load();
      showToast(t('workoutDetail.feeling.savedToast'));
      onUpdated?.();
    },
    [data, load, onUpdated, showToast, t],
  );

  const handleSportTypeChange = useCallback(
    async (typeId: number) => {
      if (!data) return;
      await workouts.updateWorkout(data.id, { workout_type_id: typeId });
      await load();
      showToast(t('workoutDetail.summary.sportUpdated'));
      onUpdated?.();
    },
    [data, load, onUpdated, showToast, t],
  );

  const handleDelete = useCallback(async () => {
    if (!data || deleting) return;
    const ok = await confirm({
      title: t('workoutDetail.deleteConfirm.title'),
      message: t('workoutDetail.deleteConfirm.message', { title: data.title }),
      variant: 'danger',
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await calendarActions.deleteWorkout(userId ?? 0, workoutId);
      showToast(t('calendar.toast.workoutDeleted', { title: data.title }));
      onDeleted?.();
      onClose();
    } catch {
      setError(t('workoutDetail.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  }, [data, deleting, userId, workoutId, onDeleted, onClose, confirm, showToast, t]);

  const editConfig: WorkoutBuilderEditConfig | undefined = editMode === 'builder' && data?.structure?.structure?.length
    ? {
        workoutId,
        workoutTypeId: data.workout_type?.id,
        initialDate: data.workout_day?.slice(0, 10) || undefined,
        initialTitle: data.title,
        initialDragData: data.structure?.structure,
        initialSteps: apiStructureToBuilderSteps(data.structure, {
          ftp: athleteFtp ?? undefined,
          ltHr: athleteLtHr ?? athleteMaxHr ?? undefined,
          primaryIntensityMetric: data.structure.primaryIntensityMetric,
        }),
        structureMeta: {
          primaryLengthMetric: data.structure.primaryLengthMetric,
          primaryIntensityMetric: data.structure.primaryIntensityMetric,
          primaryIntensityTargetOrRange: data.structure.primaryIntensityTargetOrRange,
        },
        onSaved: handleEditSaved,
        onCancel: exitEditMode,
      }
    : undefined;

  const workoutDate = data?.workout_day?.slice(0, 10) ?? '';

  const commitInlineTitle = useCallback(async () => {
    if (!data || titleSaving) return;
    const nextTitle = titleDraft.trim();
    setTitleEditing(false);
    if (!nextTitle || nextTitle === data.title) {
      setTitleDraft(data.title);
      return;
    }
    setTitleSaving(true);
    try {
      await workouts.updateWorkout(data.id, { title: nextTitle });
      setData((current) => current ? { ...current, title: nextTitle } : current);
      showToast(t('toast.saved'), 'success');
      onUpdated?.();
    } catch {
      setTitleDraft(data.title);
      showToast(t('workoutForm.error.updateFailed'), 'error');
    } finally {
      setTitleSaving(false);
    }
  }, [data, onUpdated, showToast, t, titleDraft, titleSaving]);

  const handleWorkoutDateConfirm = useCallback(async (jalaliKey: string) => {
    if (!data || dateSaving) return;
    const nextDate = jalaliKeyToIso(jalaliKey);
    setDatePickerOpen(false);
    if (!nextDate || nextDate === workoutDate) return;
    setDateSaving(true);
    try {
      await workouts.updateWorkout(data.id, { workout_day: nextDate });
      setData((current) => current ? { ...current, workout_day: nextDate } : current);
      showToast(t('toast.saved'), 'success');
      onUpdated?.();
    } catch {
      showToast(t('workoutForm.error.updateFailed'), 'error');
    } finally {
      setDateSaving(false);
    }
  }, [data, dateSaving, onUpdated, showToast, t, workoutDate]);

  useEffect(() => {
    if (initialSection !== 'records' || loading || !data || data.id !== workoutId || recordFocusedId.current === workoutId) return;
    const frame = requestAnimationFrame(() => {
      const section = recordScrollRef.current?.querySelector<HTMLElement>('.workout-annual-records');
      if (!section) return;
      recordFocusedId.current = workoutId;
      section.focus({ preventScroll: true });
      section?.scrollIntoView({ block: 'start', behavior: 'auto' });
    });
    return () => cancelAnimationFrame(frame);
  }, [initialSection, loading, data, workoutId]);

  const hasAnalyze = !!(data?.total_time != null && (data?.has_details || data?.fit_file));
  const {
    streamRaw,
    loading: streamLoading,
    error: streamError,
    reload: reloadStream,
  } = useWorkoutStream(workoutId, hasAnalyze);

  const hasStructure = !!(data?.structure?.structure?.length);

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'summary', label: t('workoutDetail.tabs.summary') },
    ...(hasAnalyze ? [{ key: 'analyze' as TabKey, label: t('workoutDetail.tabs.analyze') }] : []),
  ];

  // Portal to document.body so position:fixed covers the full viewport.
  // When rendered inside calendar-page (overflow:hidden), the panel was clipped to ~half screen.
  return createPortal(
    <>
      <div className="workout-detail-backdrop" onClick={handleBackdropClose} aria-hidden />

      <div className="workout-detail-panel">

        <WorkoutChromeHeader
          className="workout-chrome-header--floating"
          title={loading ? t('workoutDetail.loading') : (data?.title ?? '—')}
          titleContent={!loading && data ? (titleEditing ? (
            <input
              className="workout-detail-panel__header-title-input"
              value={titleDraft}
              autoFocus
              disabled={titleSaving}
              aria-label={t('workoutDetail.actions.edit')}
              onChange={(event) => setTitleDraft(event.target.value)}
              onBlur={() => void commitInlineTitle()}
              onKeyDown={(event) => {
                if (event.key === 'Enter') { event.preventDefault(); event.stopPropagation(); void commitInlineTitle(); }
                else if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setTitleDraft(data.title); setTitleEditing(false); }
              }}
            />
          ) : (
            <button type="button" className="workout-detail-panel__header-title workout-detail-panel__header-title-button" onClick={() => setTitleEditing(true)}>
              {data.title}
            </button>
          )) : undefined}
          sportEnglishName={data?.workout_type?.english_name}
          sportName={data?.workout_type?.name}
          dateContent={!loading && data?.workout_day ? (
            <button type="button" className="workout-detail-panel__header-meta-date" dir={i18n.dir()} disabled={dateSaving} onClick={() => setDatePickerOpen(true)}>
              <span dir="auto">{formatApiDateWeekday(data.workout_day, i18n.language)}</span>{' '}<bdi dir="ltr">{formatApiDateCompact(data.workout_day, i18n.language).replace(/\//g, '-')}</bdi>
            </button>
          ) : undefined}
          garminSynced={!!data?.garmin_schedule_id}
          garminSyncedLabel={t('workoutDetail.garminSynced')}
          onClose={onClose}
          closeLabel={t('common.close')}
          primaryActions={data && !loading ? (
            <div className="workout-detail-panel__header-compact-actions" ref={headerMenuRef}>
              {data && calendarWorkoutState(data as unknown as import('../services/api').CalendarWorkout, localCalendarDay()).canShare && <WorkoutChromeIconButton
                type="button"
                className="workout-detail-panel__header-btn"
                disabled={streamLoading}
                onClick={()=>setShareOpen(true)}
                title={t('workoutShare.title')}
                label={t('workoutShare.title')}
                icon={<IoShareSocialOutline aria-hidden />}
              />}
              <WorkoutChromeIconButton
                type="button"
                className={`workout-detail-panel__header-btn workout-detail-panel__header-more${headerMenuOpen ? ' workout-detail-panel__header-more--open' : ''}`}
                onClick={() => setHeaderMenuOpen((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={headerMenuOpen}
                label={t('workoutDetail.actions.more')}
                icon={<IoMenuOutline aria-hidden />}
              />
              {headerMenuOpen ? (
                <div className="workout-detail-panel__header-menu" role="menu">
                  <ActionButton
                    type="button"
                    className="workout-detail-panel__header-menu-item"
                    onClick={() => { setHeaderMenuOpen(false); handleEdit(); }}
                    role="menuitem"
                  ><IoCreateOutline aria-hidden />
                    <span>{t('workoutDetail.actions.edit')}</span></ActionButton>
                  {(data.fit_file || hasStructure) ? (
                    <ActionButton
                      type="button"
                      className="workout-detail-panel__header-menu-item"
                      onClick={() => { setHeaderMenuOpen(false); void handleDownloadStructuredFit(); }}
                      disabled={fitBusy}
                      role="menuitem"
                    >{fitDownloadLoading ? (
                        <NmLoader size="xs" inline decorative />
                      ) : (
                        <IoCloudDownloadOutline aria-hidden />
                      )}
                      <span>{t('workoutDetail.actions.downloadFit')}</span></ActionButton>
                  ) : null}
                  {hasStructure ? (
                    <ActionButton
                      type="button"
                      className="workout-detail-panel__header-menu-item"
                      onClick={() => { setHeaderMenuOpen(false); setLibrarySaved(false); setShowAddToLibrary(true); }}
                      role="menuitem"
                    ><IoFolderOpenOutline aria-hidden />
                      <span>{librarySaved ? t('workoutDetail.saved') : t('workoutDetail.actions.addToLibrary')}</span></ActionButton>
                  ) : null}
                  {data.total_time != null && comparisonNav ? (
                    <ActionButton
                      type="button"
                      className="workout-detail-panel__header-menu-item"
                      onClick={() => {
                        setHeaderMenuOpen(false);
                        comparisonNav.openComparison({
                          workoutAId: data.id,
                          workoutAUserId: userId,
                        });
                        onClose();
                      }}
                      role="menuitem"
                    ><IoGitCompareOutline aria-hidden />
                      <span>{t('workoutDetail.actions.compare')}</span></ActionButton>
                  ) : null}
                  <ActionButton
                    type="button"
                    className="workout-detail-panel__header-menu-item workout-detail-panel__header-menu-item--danger"
                    onClick={() => { setHeaderMenuOpen(false); void handleDelete(); }}
                    disabled={deleting}
                    role="menuitem"
                  ><IoTrashOutline aria-hidden />
                    <span>{deleting ? t('workoutDetail.deleting') : t('workoutDetail.actions.delete')}</span></ActionButton>
                </div>
              ) : null}
            </div>
          ) : undefined}
        />

        {!loading && !error && data && tabs.length > 1 && (
            <NmTabNav
              items={tabs.map((tabDef) => ({ value: tabDef.key, label: tabDef.label }))}
              value={tab}
              onChange={(key) => { setTab(key); setHeaderMenuOpen(false); }}
              ariaLabel={t('workoutDetail.tabs.label')}
              idBase="workout-detail-primary-tab"
              className="workout-detail-panel__primary-tabs workout-detail-panel__primary-tabs--standalone"
            />
          )}

        {!loading && data?.strava && /^\d+$/.test(data.strava.activity_id) && (
          <div style={{padding: '8px 20px', fontSize: 12}}>
            <a href={`https://www.strava.com/activities/${data.strava.activity_id}`} target="_blank" rel="noopener noreferrer" style={{color: '#FC5200', textDecoration: 'underline'}}>View on Strava</a>
            {data.strava.device_name && <span> · {data.strava.device_name}</span>}
          </div>
        )}
        {/* Scrollable content */}
        <div ref={recordScrollRef} className={`workout-detail-panel__scroll${tab === 'analyze' ? ' workout-detail-panel__scroll--analyze' : ''}`}>
          {!loading && data && <WorkoutPowerQualityCard workoutId={workoutId} />}
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120, color: TEXT_MUTED, fontSize: 13, fontWeight: 600 }}>
              <NmLoader size="lg" label={t('workoutDetail.loading')} />
            </div>
          )}
          {error && (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <div style={{ color: '#EF4444', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>{error}</div>
              <ActionButton
                onClick={() => void load()}
              >{t('workoutDetail.retry')}</ActionButton>
            </div>
          )}
          {!loading && !error && data && tab === 'summary' && (
            <MobileWorkoutSummaryTab
              autoPromptFeeling={initialSection !== 'records'}
              data={data}
              userId={userId}
              intensityThresholds={{
                ftp: athleteFtp,
                ltHr: athleteLtHr,
                maxHr: athleteMaxHr,
                thresholdSpeedMps: athleteThresholdSpeedMps,
              }}
              onEditStructure={hasStructure ? handleEditStructure : undefined}
              onCommentSubmit={handleCommentSubmit}
              onDeleteComment={handleDeleteComment}
              showCommentInvite={!isCoach}
              onFeelingSave={isCompletedWorkout(data) && !isCoach ? handleFeelingSave : undefined}
              onSportTypeChange={handleSportTypeChange}
            />
          )}
          {!loading && !error && data && tab === 'analyze' && (
            <MobileWorkoutMapTab
              workout={data}
              workoutId={workoutId}
              userId={userId}
              streamRaw={streamRaw}
              streamLoading={streamLoading}
              streamError={streamError}
              onStreamRetry={reloadStream}
              onWorkoutClick={(id) => { if (id !== workoutId) setRelatedWorkoutId(id); }}
            />
          )}
        </div>
      </div>

      {shareOpen && data && <WorkoutShareModal workout={data} stream={parseStreamData(streamRaw)} onClose={()=>setShareOpen(false)}/>}
      {relatedWorkoutId != null && (
        <WorkoutDetailPanel
          key={relatedWorkoutId}
          workoutId={relatedWorkoutId}
          userId={userId}
          onClose={() => setRelatedWorkoutId(null)}
        />
      )}
      <NmDatePickerModal
        open={datePickerOpen}
        value={isoToJalaliKey(workoutDate || new Date().toISOString().slice(0, 10))}
        onClose={() => setDatePickerOpen(false)}
        onConfirm={(value) => void handleWorkoutDateConfirm(value)}
        zIndex={1700}
      />

      {/* Form editor — non-structured workouts (name, planned, actual) */}
      {editMode === 'form' && data && data.workout_type && userId != null && (
        <WorkoutFormModal
          mode="edit"
          workoutId={workoutId}
          date={workoutDate}
          workoutTypeId={data.workout_type.id}
          workoutTypeName={getWorkoutTypeDisplayName(data.workout_type, t)}
          userId={userId}
          initial={workoutDetailToFormInitial(data)}
          onSuccess={handleEditSaved}
          onClose={exitEditMode}
        />
      )}

      {/* Fullscreen workout builder — structured workouts */}
      {editMode === 'builder' && editConfig && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1600, background: 'var(--surface-0)' }}>
          <WorkoutBuilderPage edit={editConfig} userId={userId} />
        </div>
      )}

      {/* Add structured workout to library */}
      {showAddToLibrary && data && hasStructure && (
        <AddToLibraryModal
          workoutData={workoutDetailToLibraryData(data)}
          onClose={() => setShowAddToLibrary(false)}
          onSuccess={() => setLibrarySaved(true)}
        />
      )}
    </>,
    document.body,
  );
}
